import { Check, HourglassEmpty } from '@mui/icons-material';
import {
    Box, Button, Card, CardContent, Chip, CircularProgress, Container,
    Grid, List, ListItem, ListItemIcon, ListItemText, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { PRICING } from '@/config/app.config';
import type { PlanId } from '@/domain/entities';
import { paymentsApi, userApi, ApiError } from '@/infrastructure/services/api-client';
import { useAuthStore } from '@/stores/use-auth-store';
import { useNotificationStore } from '@/stores/use-notification-store';
import { useThemeStore } from '@/stores/use-theme-store';

export function PurchasePage() {
    const { t } = useTranslation();
    const { user } = useAuthStore();
    const { addNotification } = useNotificationStore();
    const { mode } = useThemeStore();
    const [searchParams] = useSearchParams();

    const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
    const [portalLoading, setPortalLoading] = useState(false);
    const [domainAllowed, setDomainAllowed] = useState<boolean | null>(null);

    useEffect(() => {
        userApi.canSubscribe().then((res) => setDomainAllowed(res.allowed)).catch(() => setDomainAllowed(false));
    }, []);

    const handleManageSubscription = async () => {
        setPortalLoading(true);
        try {
            const { portalUrl } = await paymentsApi.createPortal();
            window.location.href = portalUrl;
        } catch {
            addNotification(t('purchase.manageSubscriptionError'), 'error');
        } finally {
            setPortalLoading(false);
        }
    };

    useEffect(() => {
        const checkout = searchParams.get('checkout');
        if (checkout === 'cancelled') {
            addNotification(t('purchase.cancelled') || 'Checkout cancelled', 'warning');
            window.history.replaceState({}, '', '/purchase');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!user || domainAllowed === null) {
        return (
            <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (!domainAllowed) {
        return (
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Card
                    sx={{
                        textAlign: 'center',
                        p: 4,
                        border: '1px solid',
                        borderColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                    }}
                >
                    <CardContent>
                        <HourglassEmpty sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                            {t('purchase.waitlist.title')}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {t('purchase.waitlist.subtitle')}
                        </Typography>
                    </CardContent>
                </Card>
            </Container>
        );
    }

    const isSubscribed = (planId: string) => user.subscriptions.includes(planId);
    const isAdminGranted = (planId: string) => user.adminGrantedPlans?.includes(planId) ?? false;

    const handleSubscribe = async (planId: PlanId) => {
        setLoadingPlan(planId);
        try {
            const result = await paymentsApi.createCheckout({ planId });
            // Redirect to Stripe Checkout
            window.location.href = result.checkoutUrl;
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            } else {
                addNotification(t('errors.generic') || 'Failed to create checkout', 'error');
            }
            setLoadingPlan(null);
        }
    };

    const plans = [
        { ...PRICING.plans.abap, planId: 'mcp-abap' as PlanId },
    ];

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {t('purchase.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('purchase.selectPlan')}
                </Typography>
            </Box>

            <Grid container spacing={4}>
                {plans.map((plan) => {
                    const subscribed = isSubscribed(plan.planId);
                    const isLoading = loadingPlan === plan.planId;
                    return (
                        <Grid size={{ xs: 12, md: 6 }} key={plan.planId}>
                            <Card
                                sx={{
                                    height: '100%',
                                    border: '1px solid',
                                    borderColor: subscribed
                                        ? 'success.main'
                                        : (mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'),
                                    position: 'relative',
                                }}
                            >
                                {subscribed && (
                                    <Chip
                                        label={t('purchase.active')}
                                        color="success"
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            top: 16,
                                            right: 16,
                                            fontWeight: 700,
                                        }}
                                    />
                                )}
                                <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                                        {plan.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        {t(plan.descriptionKey)}
                                    </Typography>

                                    <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 3, gap: 0.5 }}>
                                        <Typography variant="h3" sx={{ fontWeight: 800 }}>
                                            {PRICING.currencySymbol}{plan.price}
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary">
                                            {t('landing.pricing.perMonth')}
                                        </Typography>
                                    </Box>

                                    <List dense disablePadding sx={{ mb: 3, flexGrow: 1 }}>
                                        {plan.mcps.map((mcp) => (
                                            <ListItem key={mcp.name} disableGutters sx={{ py: 0.25 }}>
                                                <ListItemIcon sx={{ minWidth: 28 }}>
                                                    <Check sx={{ fontSize: 18, color: 'success.main' }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={mcp.name}
                                                    primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>

                                    {subscribed && isAdminGranted(plan.planId) ? (
                                        <Chip
                                            label={t('purchase.adminGranted')}
                                            color="info"
                                            variant="outlined"
                                            sx={{ fontWeight: 700, py: 2.5, borderRadius: 3, width: '100%' }}
                                        />
                                    ) : subscribed ? (
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            size="large"
                                            disabled={portalLoading}
                                            onClick={handleManageSubscription}
                                            sx={{ py: 1.5, fontWeight: 700, borderRadius: 3 }}
                                        >
                                            {portalLoading ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                t('purchase.manageSubscription')
                                            )}
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            size="large"
                                            disabled={isLoading}
                                            onClick={() => handleSubscribe(plan.planId)}
                                            sx={{ py: 1.5, fontWeight: 700, borderRadius: 3 }}
                                        >
                                            {isLoading ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                t('purchase.subscribe')
                                            )}
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </Container>
    );
}

export default PurchasePage;
